import { useGameStore } from '../store/useGameStore';
import { COLOR_LABELS, COLOR_CSS, ELEMENT_LABELS, ELEMENT_CSS, RESONANCE_LABELS, SPIRIT_SKILL_LABELS } from '../data/attributes';
import type { AttrEntry, ElementType } from '../types';
import './LeftPanel.css';

export function LeftPanel() {
    const { totalAttrs, stones, placedStones } = useGameStore();

    // 按类别分组属性
    const grouped = {
        basic: totalAttrs.filter(a => a.category === 'basic'),
        advanced: totalAttrs.filter(a => a.category === 'advanced'),
        element: totalAttrs.filter(a => a.category === 'element'),
        resonance: totalAttrs.filter(a => a.category === 'resonance'),
    };

    const getElementIcon = (elem?: ElementType) => {
        switch (elem) {
            case 'fire': return '🔥';
            case 'ice': return '❄️';
            case 'wood': return '🌿';
            case 'thunder': return '⚡';
            default: return '◆';
        }
    };

    // 统计已放置的石头信息
    const placedStoneInfos = Object.entries(placedStones).map(([stoneId, pos]) => {
        const stone = stones.find(s => s.id === stoneId);
        return stone ? { stone, pos } : null;
    }).filter(Boolean);

    return (
        <div className="left-panel">
            <div className="panel-title">📊 属性技能汇总</div>

            {/* 放置统计 */}
            <div className="summary-section">
                <div className="section-label">放置统计</div>
                <div className="stat-row">
                    <span>已放置石头</span>
                    <span className="stat-value">{Object.keys(placedStones).length} 块</span>
                </div>
                <div className="stat-row">
                    <span>已占用格子</span>
                    <span className="stat-value">{Object.keys(placedStones).length * 4} 格</span>
                </div>
            </div>

            {/* 基础属性 */}
            {grouped.basic.length > 0 && (
                <div className="summary-section">
                    <div className="section-label">📦 基础属性</div>
                    <div className="attr-grid">
                        {grouped.basic.map(attr => (
                            <div key={attr.id} className="attr-item basic">
                                <span className="attr-label">{attr.label}</span>
                                <span className="attr-value">+{attr.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 进阶属性 */}
            {grouped.advanced.length > 0 && (
                <div className="summary-section">
                    <div className="section-label">⚔️ 进阶属性</div>
                    <div className="attr-grid">
                        {grouped.advanced.map(attr => (
                            <div key={attr.id} className="attr-item advanced">
                                <span className="attr-label">{attr.label}</span>
                                <span className="attr-value">+{attr.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 元素属性 */}
            {grouped.element.length > 0 && (
                <div className="summary-section">
                    <div className="section-label">✨ 四元素属性</div>
                    <div className="attr-grid">
                        {grouped.element.map(attr => (
                            <div key={attr.id} className="attr-item element" style={{ borderLeftColor: ELEMENT_CSS[attr.element!] }}>
                                <span className="attr-icon">{getElementIcon(attr.element)}</span>
                                <span className="attr-label">{attr.label}</span>
                                <span className="attr-value">+{attr.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 灵蕴共鸣技能 */}
            {grouped.resonance.length > 0 && (
                <div className="summary-section">
                    <div className="section-label">🌀 灵蕴共鸣 / 灵蕴技</div>
                    <div className="resonance-list">
                        {grouped.resonance.map(attr => (
                            <div key={attr.id} className="resonance-item" style={{ borderLeftColor: ELEMENT_CSS[attr.element!] }}>
                                <span className="resonance-icon">{getElementIcon(attr.element)}</span>
                                <span className="resonance-name">{attr.label}</span>
                                {attr.spiritSkill && (
                                    <span className="resonance-tag">灵蕴技</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {totalAttrs.length === 0 && (
                <div className="empty-hint">尚未放置任何石头，<br />请在右侧录入石头并拖放到棋盘上</div>
            )}

            {/* 已放置石头列表 */}
            {placedStoneInfos.length > 0 && (
                <div className="summary-section">
                    <div className="section-label">已放置石头</div>
                    <div className="placed-list">
                        {placedStoneInfos.map(({ stone, pos }) => (
                            <div key={stone.id} className="placed-item">
                <span className="placed-color" style={{ color: COLOR_CSS[stone.color] }}>
                  ●
                </span>
                                <span className="placed-name">{stone.type === 'craft' ? '匠心石' : '机巧石'}</span>
                                <span className="placed-pos">({pos.row},{pos.col})</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}